/**
 * jw-product-pricing.js
 * Phase F2.5.1 — Pricing Adapter (Compiler fact consumer, zero DOM)
 *
 * 职责：
 *   Tier Resolver:   qty → tier selection → Compiler.price (cents)
 *   Marketing Engine: variant.price / compare_at → discount %
 *
 * Compiler Fact Boundary:
 *   Tier prices are Compiler-generated facts. This module selects, not calculates.
 *   It MUST NOT derive, mutate, or recalculate tier prices at runtime.
 *   tier_discount_rate in tiers[] is audit-only — not consumed by any display logic.
 *
 * 依赖：
 *   JWProductContext  (read-only: pricing.tiers, variants.list, variants.byId, product.moq)
 *   JWProductState    (read-only: activeSku, qtys)
 *   tierData          (Compiler-generated facts: priceRaw {skuId: {tiers: [{min, price, tier_discount_rate}]}})
 *
 * 🚫 禁止：
 *   - 根据 base_price 重新生成 tiers
 *   - 根据 tier_discount_rate 反向计算价格
 *   - 修改 tier_policy 或 visible tiers
 *   - DOM 操作 / Cart mutation / Variant selection / Gallery / Image 逻辑
 *
 * Contract: docs/contracts/product_pricing_v1.md
 */
(function () {
  'use strict';

  var _ctx      = null;
  var _state    = null;
  var _tierData = null;  // priceRaw — per-SKU tier detail

  /**
   * Initialize pricing module.
   *
   * @param {object} context  — JWProductContext
   * @param {object} state    — JWProductState
   * @param {object} tierData — priceRaw: {skuId: {tiers: [{min, price, tier_discount_rate}]}}
   * @returns {object} JWProductPricing API
   */
  function init(context, state, tierData) {
    _ctx      = context || {};
    _state    = state || {};
    _tierData = tierData || {};
    return window.JWProductPricing;
  }

  // ═══════════════ Tier Resolver (Compiler facts → selection) ═══════════════

  /**
   * Get tier array for a specific SKU.
   * Falls back to global context.pricing.tiers (transformed from PD.tiers).
   *
   * @param {string} skuId
   * @returns {Array<{min:number, price:number, tier_discount_rate:number}>}
   */
  function _getTiers(skuId) {
    if (skuId && _tierData[skuId] && _tierData[skuId].tiers && _tierData[skuId].tiers.length > 0) {
      return _tierData[skuId].tiers;
    }
    // Fallback: global tiers from Context
    var globalTiers = (_ctx.pricing && _ctx.pricing.tiers) ? _ctx.pricing.tiers : {};
    var keys = Object.keys(globalTiers).sort(function (a, b) { return Number(a) - Number(b); });
    return keys.map(function (k) {
      return { min: Number(k), price: globalTiers[k] / 100, tier_discount_rate: 0 };
    });
  }

  /**
   * Get tier price for a SKU at a given quantity.
   * All internal math in cents.
   *
   * @param {string} skuId      — SKU ID
   * @param {number} baseCents  — fallback price in cents
   * @param {number} qty        — quantity
   * @returns {number} price in cents
   */
  function getTierPrice(skuId, baseCents, qty) {
    baseCents = Number(baseCents) || 0;
    var tiers = _getTiers(skuId);

    if (qty <= 0) {
      // q≤0: return base tier (min=1) price
      if (tiers && tiers.length > 0) {
        var tp = Number(tiers[0].price);
        if (isFinite(tp) && tp > 0) return Math.round(tp * 100);
      }
      return baseCents;
    }

    // Match tier by quantity (right-to-left, first match wins)
    var matched = null;
    for (var i = tiers.length - 1; i >= 0; i--) {
      if (qty >= Number(tiers[i].min)) { matched = tiers[i]; break; }
    }
    if (matched && matched.price != null) {
      var tp2 = Number(matched.price);
      if (isFinite(tp2) && tp2 > 0) return Math.round(tp2 * 100);
    }
    return baseCents;
  }

  /**
   * Display-layer convenience: compute tier discount % from already-resolved prices.
   * This is a UI approximation — NOT the same as tiers[].tier_discount_rate (audit-only).
   * Formula: (1 - resolvedCompilerPrice / basePrice) * 100
   *
   * @param {string} skuId
   * @param {number} baseCents — fallback price
   * @param {number} qty
   * @returns {number} discount percentage (integer), UI approximation only
   */
  function getTierDiscount(skuId, baseCents, qty) {
    if (baseCents <= 0) return 0;
    var saleCents = getTierPrice(skuId, baseCents, qty);
    if (saleCents < baseCents) return Math.round((1 - saleCents / baseCents) * 100);
    return 0;
  }

  /**
   * Get the active tier index for a SKU at a given quantity.
   *
   * @param {string} skuId
   * @param {number} qty
   * @returns {number} tier index (0-based)
   */
  function getActiveTierIndex(skuId, qty) {
    var tiers = _getTiers(skuId);
    if (!tiers || tiers.length === 0) return 0;
    for (var i = tiers.length - 1; i >= 0; i--) {
      if (qty >= Number(tiers[i].min)) return i;
    }
    return 0;
  }

  // ═══════════════ Marketing Engine ═══════════════

  /**
   * Look up variant by SKU ID from Context.
   */
  function _lookupVariant(skuId) {
    if (!_ctx.variants || !_ctx.variants.byId) return null;
    return _ctx.variants.byId[skuId] || null;
  }

  /**
   * Get variant price in cents (Shopify authority).
   *
   * @param {string} skuId
   * @returns {number} cents
   */
  function getVariantPrice(skuId) {
    var v = _lookupVariant(skuId);
    if (!v) return 0;
    // Context-normalized variant has price in cents
    return v.price || 0;
  }

  /**
   * Get compare-at price in cents.
   *
   * @param {string} skuId
   * @returns {number} cents
   */
  function getCompareAtPrice(skuId) {
    var v = _lookupVariant(skuId);
    if (!v) return 0;
    return v.compare_at_price || 0;
  }

  /**
   * Get marketing discount percentage for a SKU.
   * Based on Shopify variant.price vs variant.compare_at_price.
   * Independent of tier pricing.
   *
   * @param {string} skuId
   * @returns {number} percentage (integer), 0 if no discount
   */
  function getDiscountPct(skuId) {
    var price     = getVariantPrice(skuId);
    var compareAt = getCompareAtPrice(skuId);
    if (compareAt <= 0 || price <= 0 || compareAt <= price) return 0;
    return Math.round((1 - price / compareAt) * 100);
  }

  /**
   * Compute global maximum marketing discount across all variants.
   * Mirrors initTierPriceSystem()'s globalT1Disc logic.
   *
   * @returns {number} max discount percentage
   */
  function getGlobalMaxDiscount() {
    var list = (_ctx.variants && _ctx.variants.list) ? _ctx.variants.list : [];
    var maxD = 0;
    for (var i = 0; i < list.length; i++) {
      var v    = list[i];
      var p    = v.price || 0;
      var cmp  = v.compare_at_price || 0;
      if (cmp > 0 && p > 0 && cmp > p) {
        var d = Math.round((1 - p / cmp) * 100);
        if (d > maxD) maxD = d;
      }
    }
    return maxD;
  }

  // ═══════════════ Convenience ═══════════════

  /**
   * Get base price for a SKU (variant.price cents).
   * Alias for getVariantPrice.
   */
  function getBasePrice(skuId) {
    return getVariantPrice(skuId);
  }

  /**
   * Get MOQ from Context.
   */
  function getMOQ() {
    return (_ctx.product && _ctx.product.moq) ? _ctx.product.moq : 1;
  }

  /**
   * Get tiers array for a SKU (public).
   */
  function getTiers(skuId) {
    return _getTiers(skuId);
  }

  // ═══════════════ Resolved Facts (Display consumer interface) ═══════════════

  /**
   * Get resolved pricing facts for a SKU at a given quantity.
   * This is the Display layer's single entry point — it receives Compiler facts
   * already resolved, never re-derives them.
   *
   * @param {string} skuId
   * @param {number} qty
   * @returns {object} {
   *   skuId, tiers, activeTier, activeTierIndex,
   *   resolvedPrice: { cents, dollars },
   *   basePrice: { cents, dollars },
   *   compareAtPriceDollars,  // v1.1: Marketing Engine authority (variant.compare_at_price)
   *   discountPercent         // UI approximation — NOT tier_discount_rate
   * }
   */
  function getResolvedFacts(skuId, qty) {
    var tiers     = _getTiers(skuId);
    var baseCents = getBasePrice(skuId);
    var resCents  = getTierPrice(skuId, baseCents, qty);
    var activeIdx = getActiveTierIndex(skuId, qty);
    var activeTier = (tiers && tiers[activeIdx]) ? tiers[activeIdx] : (tiers && tiers[0]) ? tiers[0] : null;

    // UI approximation: display discount from resolved vs base price
    // NOT tier_discount_rate (audit-only)
    var discountPercent = 0;
    if (baseCents > 0 && resCents < baseCents) {
      discountPercent = Math.round((1 - resCents / baseCents) * 100);
    }

    // v1.1: Marketing Engine compare_at_price — independent of tier pricing
    var compareAtCents = getCompareAtPrice(skuId);
    var compareAtDollars = compareAtCents > 0 ? Number((compareAtCents / 100).toFixed(2)) : 0;

    return {
      skuId:                skuId,
      tiers:                tiers,
      activeTier:           activeTier,
      activeTierIndex:      activeIdx,
      resolvedPrice:        { cents: resCents, dollars: (resCents / 100).toFixed(2) },
      basePrice:            { cents: baseCents, dollars: (baseCents / 100).toFixed(2) },
      compareAtPriceDollars: compareAtDollars,
      discountPercent:      discountPercent
    };
  }

  // ── Expose ──
  window.JWProductPricing = {
    init:                init,
    // Tier Resolver
    getTierPrice:        getTierPrice,
    getTierDiscount:     getTierDiscount,
    getActiveTierIndex:  getActiveTierIndex,
    getTiers:            getTiers,
    // Resolved Facts (Display consumer)
    getResolvedFacts:    getResolvedFacts,
    // Marketing Engine
    getVariantPrice:     getVariantPrice,
    getCompareAtPrice:   getCompareAtPrice,
    getDiscountPct:      getDiscountPct,
    getGlobalMaxDiscount: getGlobalMaxDiscount,
    // Convenience
    getBasePrice:        getBasePrice,
    getMOQ:              getMOQ
  };

})();
